import * as React from 'react'
import { Route, Redirect, useHistory, useLocation } from 'react-router-dom'
import { IonSplitPane, IonRouterOutlet, isPlatform } from '@ionic/react'
import { LocalNotifications } from '@capacitor/local-notifications'
import { PushNotifications } from '@capacitor/push-notifications'
import { useQueryClient } from '@tanstack/react-query'

import { updateService } from './services/updateService'
import { useAccount } from './contexts/authContext'
import { chatQueryKeys } from './hooks/chat/queries'
import AuthCallback from './pages/auth-callback'
import Sequence from './pages/sequence/Sequence'
import AddClass from './pages/actions/AddClass'
import NextContact from './pages/next-contact'
import Inbox from './pages/chat/Inbox'
import ConversationDetail from './pages/chat/conversation-detail'
import Search from './pages/search'
import StatsStylist from './pages/admin/stylist-stats'
import SwitchAccount from './pages/account/switch-account'
import AdminTemplates from './pages/admin/templates/templates'
import AdminTemplateDetail from './pages/admin/templates/admin-template-detail'
import CreateStoreTemplate from './pages/admin/templates/create-store-template'
import Templates from './pages/templates/templates'
import TemplateDetail from './pages/templates/template-detail'
import CreateTemplate from './pages/templates/create-template'
import CreateBroadcast from './pages/broadcasts/create-broadcast'
import BroadcastClients from './pages/broadcasts/broadcast-clients'
import CallListDetail from './pages/admin/call-list-detail'
import AdminBroadcastList from './pages/admin/broadcasts/admin-broadcast-list'
import AdminPullBroadcast from './pages/admin/broadcasts/admin-pull-broadcast'
import AdminInbox from './pages/admin/chat/inbox'
import BroadcastList from './pages/broadcasts/broadcast-list'
import ClientImport from './pages/client-import'
import Menu from './components/menu'
import AccountPage from './pages/account/account-page'
import Stats from './pages/stats'
import WhatHappened from './pages/what-happened'
import Outreach from './pages/admin/outreach'
import ClientDetail from './pages/client/ClientDetail'
import CreateOutreach from './pages/admin/outreach/create-outreach'
import AdminDynamicOutreachDetail from './pages/admin/outreach/admin-dynamic-outreach-detail'
import CreatePostPage from './pages/post/create-post-page'
import BroadcastDetailPage from './pages/broadcasts/broadcast-detail'
import BroadcastDetailClientsPage from './pages/broadcasts/broadcast-detail-clients'
import AdminBroadcastDetailPage from './pages/admin/broadcasts/admin-broadcast-detail'
import AdminBroadcastDetailClientsPage from './pages/admin/broadcasts/admin-broadcast-detail-clients'
import Tasks from './pages/tasks/tasks'
import TaskDetail from './pages/tasks/task-detail'
import TempAppTabs from './components/temp-app-tabs'
import ConnectionsPage from './pages/account/connections-page'
import GroupConnectionsPage from './pages/account/connections/groups-connections-page'
import UsersConnectionsPage from './pages/account/connections/users-connections-page'
import PostListPage from './pages/posts/post-list-page'
import PostDetailPage from './pages/posts/post-detail-page'
import ApprovalsPage from './pages/admin/approvals-page'
import RoleSocialsPage from './pages/admin/role-socials-page'
import { SocialApp } from './social-app'

import NoAccess from './pages/no-access'
import AutomationsListPage from './pages/admin/automations/automations-list-page'
import AutomationDetailPage from './pages/admin/automations/automation-detail-page'
import CustomerOptInPage from './pages/admin/customer/opt-in'
import CustomerSuccessPage from './pages/admin/customer/success'
import CashierMoreInfoPage from './pages/admin/customer/more-info'
import CreateHtmlEmailPage from './pages/admin/broadcasts/create-html-email-page'
import HeartlandRedemptionPage from './pages/heartland/heartland-redemption-page'
import HeartlandOptInPage from './pages/heartland/heartland-opt-in-page'
import HeartlandOptInSuccessPage from './pages/heartland/heartland-opt-in-success-page'
import HeartlandOptInMoreInfoPage from './pages/heartland/heartland-opt-in-more-info-page'
import Home from './pages/home'
import { PrivateRoute } from './auth/private-route'
import { NotAllowed } from './auth/not-allowed'
import StylistsSetupPage from './pages/admin/stylists-setup'
import DashboardPage from './pages/admin/dashboard-page'
import { isSocialOnly, useIsStore } from './auth/utils'
import CreateAutomationPage from './pages/admin/automations/create-automation-page'
import CreateAutomationHtmlEmailPage from './pages/admin/automations/create-automation-html-email-page'
import LightspeedRedemptionPage from './pages/lightspeed/lightspeed-redemption-page'
import LightspeedOptInPage from './pages/lightspeed/lightspeed-opt-in-page'
import LightspeedOptInSuccessPage from './pages/lightspeed/lightspeed-opt-in-success-page'
import LightspeedOptInMoreInfoPage from './pages/lightspeed/lightspeed-opt-in-more-info-page'
import LoyaltyRedemptionPage from './pages/admin/customer/loyalty-redemption-page'
import LoyaltyProgramsPage from './pages/loyalty/loyalty-programs-page'
import LoyaltyProgramsDetailPage from './pages/loyalty/loyalty-programs-detail-page'
import { AddAccount } from './add-account'
import { CashierCustomerCheckInPage } from './pages/admin/cashier/customer-check-in-page'
import { CashierAddCustomerPage } from './pages/admin/cashier/add-customer-page'
import { CashierRedeemLoyaltyPage } from './pages/admin/cashier/redeem-loyalty-page'
import { PhoneInputPage } from './pages/admin/customer/check-in/phone-input-page'
import { MoreInfoPage } from './pages/admin/customer/check-in/more-info-page'
import { LoyaltyInfoPage } from './pages/admin/customer/check-in/loyalty-info-page'

const disabledPaths = ['heartland', 'cashier', 'customer', 'lightspeed']

function useRouteChange() {
  const location = useLocation()
  const [currentPath, setCurrentPath] = React.useState(location.pathname)

  React.useEffect(() => {
    const pathname = location.pathname

    setCurrentPath(pathname)
  }, [location.pathname])

  return currentPath
}

function AuthenticatedApp() {
  const account = useAccount()

  const { isUno } = useIsStore()

  const history = useHistory()
  const queryClient = useQueryClient()

  const path = useRouteChange()

  React.useEffect(() => {
    if (isPlatform('capacitor')) {
      PushNotifications.addListener('registration', (token) => {
        updateService.updateInstalation(token.value)
      })

      // Some issue with your setup and push will not work
      PushNotifications.addListener('registrationError', (error) => {
        console.log('error', JSON.stringify(error))
        alert('Error on registration: ' + JSON.stringify(error))
      })

      // Show us the notification payload if the app is open on our device
      PushNotifications.addListener(
        'pushNotificationReceived',
        (notification) => {
          if (isPlatform('android')) {
            LocalNotifications.schedule({
              notifications: [
                {
                  title: notification.title ?? '',
                  body: notification.body ?? '',
                  id: 1,
                },
              ],
            })
          }
        }
      )

      // Method called when tapping on a notification
      PushNotifications.addListener(
        'pushNotificationActionPerformed',
        (notification) => {
          let path

          if (notification?.notification?.data?.notification) {
            const notificationDetail = JSON.parse(
              notification?.notification?.data?.notification
            )

            const likesClientId =
              notificationDetail?.likes?.[0]?.client?.objectId
            if (likesClientId) {
              path = `/tabs/clients/client/${likesClientId}?currentview=wishlist`
            }
          } else if (notification?.notification?.data?.conversationId) {
            // invalidate conversation and refetch
            queryClient.invalidateQueries(
              chatQueryKeys.conversationDetail(
                notification.notification.data.conversationId
              )
            )
            path = `/chat/c/${notification.notification.data.conversationId}`
          } else if (notification?.notification?.data?.clientId) {
            path = `/tabs/clients/client/${notification.notification.data.clientId}`
          }

          if (!path) return
          if (history.length === 1) {
            history.replace(path)
          } else {
            history.push(path)
          }
        }
      )

      return function cleanUp() {
        PushNotifications.removeAllListeners()
      }
    }
  }, [history, queryClient])

  const isSplitMenuDisabled = disabledPaths.some((p) => path.includes(p))

  // HACK: ACCOUNT EMPTY OBJECT WORKAROUND
  if (!Object.keys(account).length) return null
  if (!account.account?.objectId) return <AddAccount />

  if (isSocialOnly(account)) {
    return <SocialApp />
  }

  return (
    <IonSplitPane contentId="main" disabled={isSplitMenuDisabled}>
      <Menu />
      <IonRouterOutlet id="main">
        <Route path="/tabs" render={() => <TempAppTabs />} />

        {/* Admin Home */}
        <Route exact path="/admin/home">
          <Home />
        </Route>

        {/* Templates */}
        <PrivateRoute
          exact
          path="/admin/templates"
          requiredSettings={['clienteling', 'marketing']}
        >
          <AdminTemplates />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/templates/create"
          requiredSettings={['clienteling', 'marketing']}
        >
          <CreateStoreTemplate />
        </PrivateRoute>
        <PrivateRoute
          exact
          requiredSettings={['clienteling', 'marketing']}
          path="/admin/templates/detail/:tid"
        >
          <AdminTemplateDetail />
        </PrivateRoute>

        {/* ADMIN AUTOMATIONS */}
        <PrivateRoute
          exact
          path="/admin/automations/"
          requiredSettings={['marketing']}
        >
          <AutomationsListPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/automations/create/:templateId/"
          requiredSettings={['marketing']}
        >
          <CreateAutomationPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/automations/detail/:automationId"
          requiredSettings={['marketing']}
        >
          <AutomationDetailPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/automations/detail/:automationId/step/:stepId/html-email"
          requiredSettings={['marketing']}
        >
          <CreateAutomationHtmlEmailPage />
        </PrivateRoute>

        {/* Client Import */}
        <PrivateRoute
          exact
          path="/admin/import"
          requiredSettings={['clienteling', 'marketing']}
        >
          <ClientImport />
        </PrivateRoute>

        {/* ********* */}
        {/* SOCIAL  */}
        {/* ********* */}
        {/* Approvals */}
        <PrivateRoute exact path="/admin/posts" requiredSettings={['social']}>
          <PostListPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/approvals"
          requiredSettings={['social']}
        >
          <ApprovalsPage />
        </PrivateRoute>
        {/* HOMEPAGE POST FLOW */}
        <PrivateRoute exact path="/post/create/" requiredSettings={['social']}>
          <CreatePostPage />
        </PrivateRoute>

        {/* *********** */}
        {/* CLIENTELING */}
        {/* *********** */}
        {/* Outreach */}
        <PrivateRoute
          exact
          path="/admin/outreach"
          requiredSettings={['clienteling']}
        >
          <Outreach />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/outreach/create"
          requiredSettings={['clienteling']}
        >
          <CreateOutreach />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/outreach/dynamic/:outreachId"
          requiredSettings={['clienteling']}
        >
          <AdminDynamicOutreachDetail backRef="/admin/outreach" />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/outreach/onetime/:id"
          requiredSettings={['clienteling']}
        >
          <CallListDetail />
        </PrivateRoute>
        <Route exact path="/admin/outreach/*/client/:id">
          <ClientDetail />
        </Route>

        {/* ******* */}
        {/* LOYALTY */}
        {/* ******* */}
        <Route exact path="/admin/loyalty-programs">
          <LoyaltyProgramsPage />
        </Route>
        <Route exact path="/admin/loyalty-programs/detail/:loyaltyId">
          <LoyaltyProgramsDetailPage />
        </Route>

        {/* ********* */}
        {/* ANALYTICS */}
        {/* ********* */}
        <PrivateRoute
          exact
          path="/admin/dashboard"
          requiredSettings={['clienteling', 'marketing']}
        >
          <DashboardPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/stats/stylist"
          requiredSettings={['clienteling']}
        >
          <StatsStylist />
        </PrivateRoute>

        {/* ********* */}
        {/* ASSOCIATE */}
        {/* ********* */}
        {/* My Clients */}
        <Route exact path="/*/client/:id">
          <ClientDetail />
        </Route>
        {/* My Templates */}
        <PrivateRoute
          exact
          path="/templates"
          requiredSettings={['clienteling']}
        >
          <Templates />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/templates/create"
          requiredSettings={['clienteling']}
        >
          <CreateTemplate />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/templates/detail/:tid"
          requiredSettings={['clienteling']}
        >
          <TemplateDetail />
        </PrivateRoute>
        {/* Tasks */}
        <PrivateRoute exact path="/tasks" requiredSettings={['clienteling']}>
          <Tasks />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/tasks/:className/:objectId"
          requiredSettings={['clienteling']}
        >
          <TaskDetail />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/add/:className"
          requiredSettings={['clienteling']}
        >
          <AddClass />
        </PrivateRoute>
        {/*  My Broadcasts */}
        <PrivateRoute
          exact
          path="/broadcasts"
          requiredSettings={['clienteling']}
        >
          {isUno() ? <NoAccess title="Broadcasts" /> : <BroadcastList />}
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/broadcasts/create"
          requiredSettings={['clienteling']}
        >
          <CreateBroadcast />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/broadcasts/create/clients"
          requiredSettings={['clienteling']}
        >
          <BroadcastClients />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/broadcasts/create/clients/client/:id"
          requiredSettings={['clienteling']}
        >
          <ClientDetail />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/broadcasts/detail/:broadcastId"
          requiredSettings={['clienteling']}
        >
          <BroadcastDetailPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/broadcasts/detail/:broadcastId/clients"
          requiredSettings={['clienteling']}
        >
          <BroadcastDetailClientsPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/broadcasts/detail/:broadcastId/clients/client/:id"
          requiredSettings={['clienteling']}
        >
          <ClientDetail />
        </PrivateRoute>
        {/* My Posts */}
        <PrivateRoute exact requiredSettings={['social']} path="/posts">
          <PostListPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/posts/detail/:postId"
          requiredSettings={['social']}
        >
          <PostDetailPage />
        </PrivateRoute>
        {/* My Stats */}
        <PrivateRoute exact path="/stats" requiredSettings={['clienteling']}>
          <Stats />
        </PrivateRoute>

        {/* ******** */}
        {/* Check-In */}
        {/* ******** */}
        {/* Rotate (Customer Opt-In) */}
        <PrivateRoute
          exact
          path="/admin/customer/opt-in"
          requiredSettings={['clienteling', 'marketing']}
        >
          <CustomerOptInPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/customer/opt-in/more-info"
          requiredSettings={['clienteling', 'marketing']}
        >
          <CashierMoreInfoPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/customer/opt-in/loyalty"
          requiredSettings={['clienteling', 'marketing']}
        >
          <LoyaltyRedemptionPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/customer/opt-in/success"
          requiredSettings={['clienteling', 'marketing']}
        >
          <CustomerSuccessPage />
        </PrivateRoute>

        {/* Cashier Customer Check-In */}
        <PrivateRoute
          exact
          path="/admin/cashier/customer-check-in"
          requiredSettings={['clienteling', 'marketing']}
        >
          <CashierCustomerCheckInPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/cashier/add-customer"
          requiredSettings={['clienteling', 'marketing']}
        >
          <CashierAddCustomerPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/cashier/redeem-loyalty/:clientId"
          requiredSettings={['clienteling', 'marketing']}
        >
          <CashierRedeemLoyaltyPage />
        </PrivateRoute>

        {/* Customer-Facing Check-In */}
        <PrivateRoute
          exact
          path="/admin/customer/check-in/phone"
          requiredSettings={['clienteling', 'marketing']}
        >
          <PhoneInputPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/customer/check-in/more-info"
          requiredSettings={['clienteling', 'marketing']}
        >
          <MoreInfoPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/customer/check-in/loyalty-info/:clientId"
          requiredSettings={['clienteling', 'marketing']}
        >
          <LoyaltyInfoPage />
        </PrivateRoute>

        {/* ******* */}
        {/* ACCOUNT */}
        {/* ******* */}
        <Route path="/account" component={AccountPage} exact />
        {/* Switch Account */}
        <Route exact path="/account/switch-accounts">
          <SwitchAccount />
        </Route>
        {/* Social Roles Setup */}
        <PrivateRoute exact path="/admin/roles" requiredSettings={['social']}>
          <RoleSocialsPage />
        </PrivateRoute>
        {/* Social Setup */}
        <PrivateRoute
          exact
          path="/account/connections"
          requiredSettings={['social']}
        >
          <ConnectionsPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/account/connections/groups"
          requiredSettings={['social']}
        >
          <GroupConnectionsPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/account/connections/groups/:groupId"
          requiredSettings={['social']}
        >
          <UsersConnectionsPage />
        </PrivateRoute>
        <PrivateRoute
          exact
          path="/admin/stylist"
          requiredSettings={['clienteling']}
        >
          <StylistsSetupPage />
        </PrivateRoute>

        {/* ************ */}
        {/* OTHER ROUTES */}
        {/* ************ */}
        <Route exact path="/authorization/callback" component={AuthCallback} />

        <Route exact path="/search">
          <Search />
        </Route>

        {/* ASSOCIATE INBOX */}
        <Route exact path="/chat/inbox">
          <Inbox />
        </Route>
        <Route exact path="/chat/c/:cid">
          <ConversationDetail />
        </Route>

        <Route exact path="/sequence">
          <Sequence />
        </Route>

        {/* Heartland Routes */}
        <Route exact path="/heartland/:groupId/redeem-loyalty">
          <HeartlandRedemptionPage />
        </Route>
        <Route exact path="/heartland/:groupId/opt-in">
          <HeartlandOptInPage />
        </Route>
        <Route exact path="/heartland/:groupId/opt-in/success">
          <HeartlandOptInSuccessPage />
        </Route>
        <Route exact path="/heartland/:groupId/opt-in/more-info">
          <HeartlandOptInMoreInfoPage />
        </Route>

        {/* Lightspeed Routs */}
        <Route exact path="/lightspeed/redeem-loyalty">
          <LightspeedRedemptionPage />
        </Route>
        <Route exact path="/lightspeed/opt-in">
          <LightspeedOptInPage />
        </Route>
        <Route exact path="/lightspeed/opt-in/success">
          <LightspeedOptInSuccessPage />
        </Route>
        <Route exact path="/lightspeed/opt-in/more-info">
          <LightspeedOptInMoreInfoPage />
        </Route>

        <Route path="/WhatHappened/:clientId">
          <WhatHappened />
        </Route>
        <Route exact path="/NextContact/:clientId" component={NextContact} />

        {/* Admin Inbox */}
        <Route exact path="/admin/chat/inbox">
          <AdminInbox />
        </Route>
        <Route exact path="/admin/chat/c/:cid">
          <ConversationDetail />
        </Route>

        <Route exact path="/access-denied">
          <NotAllowed />
        </Route>

        <Redirect exact from="/" to="/tabs" />
      </IonRouterOutlet>
    </IonSplitPane>
  )
}

export default AuthenticatedApp
